// src/pages/public/VillaDetailPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../components/layout/MainLayout';
import Loading from '../../components/common/Loading';
import { getPropertyById } from '../../services/villa';
import { formatCurrency } from '../../utils/formatters';
import {
  X, ChevronLeft, ChevronRight, MapPin, Star, Bed, Bath, Users,
  Wifi, Wind, Waves, UtensilsCrossed, Tv, Dumbbell, Coffee,
  Gamepad2, Refrigerator, Lock, Car, Flower2, Shirt, Sparkles,
  CheckCircle2, Shield, Clock, ArrowLeft, ZoomIn,
} from 'lucide-react';

/* ── Color tokens ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray300:    '#d1d5db',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
  stone50:    '#fafaf9',
  green:      '#16a34a',
  greenHover: '#15803d',
};

/* ── useInView hook ── */
const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ── Animated wrapper ── */
const Anim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const from = { up: 'translateY(36px)', left: 'translateX(-36px)', right: 'translateX(36px)', scale: 'scale(0.9)' };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : from[dir],
      transition: `opacity 0.75s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.75s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── Primary Button ── */
const BtnPrimary = ({ onClick, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '13px 24px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: hov ? C.amberHover : C.amber,
        color: C.white, fontWeight: 600, fontSize: 15, fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'none',
        boxShadow: hov ? '0 12px 28px rgba(217,119,6,0.45)' : '0 4px 14px rgba(217,119,6,0.28)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        ...style,
      }}>
      {children}
    </button>
  );
};

/* ── WhatsApp Button ── */
const BtnWhatsApp = ({ onClick, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
        width: '100%', padding: '16px 24px', borderRadius: 16, border: 'none', cursor: 'pointer',
        background: hov
          ? 'linear-gradient(135deg, #15803d, #166534)'
          : 'linear-gradient(135deg, #16a34a, #15803d)',
        color: C.white, fontWeight: 700, fontSize: 16, fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px) scale(1.02)' : 'none',
        boxShadow: hov ? '0 16px 36px rgba(22,163,74,0.45)' : '0 6px 20px rgba(22,163,74,0.32)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        ...style,
      }}>
      {children}
    </button>
  );
};

/* ── Icon button (nav arrows) ── */
const IconBtn = ({ onClick, children, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer',
        backgroundColor: hov ? C.amberLight : C.white,
        color: C.gray900,
        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        transform: hov ? 'scale(1.1)' : 'scale(1)',
        transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
        ...style,
      }}>
      {children}
    </button>
  );
};

/* ── Stat card ── */
const StatCard = ({ emoji, value, label }) => {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        textAlign: 'center', padding: '20px 12px',
        backgroundColor: hov ? '#fffbeb' : C.amberLight,
        borderRadius: 16, border: `1px solid ${hov ? C.amber : '#fde68a'}`,
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        transform: hov ? 'translateY(-4px)' : 'none',
        boxShadow: hov ? '0 8px 24px rgba(217,119,6,0.18)' : 'none',
        cursor: 'default',
      }}>
      <div style={{ fontSize: 36, marginBottom: 6 }}>{emoji}</div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, color: C.gray900, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, color: C.gray500, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'DM Sans', sans-serif" }}>{label}</div>
    </div>
  );
};

/* ── Facility icon map ── */
const getFacilityIcon = (name) => {
  const n = name.toLowerCase();
  if (n.includes('wifi') || n.includes('internet')) return <Wifi size={20} color={C.amber} />;
  if (n.includes('ac') || n.includes('air conditioning')) return <Wind size={20} color={C.amber} />;
  if (n.includes('pool') || n.includes('kolam') || n.includes('renang')) return <Waves size={20} color={C.amber} />;
  if (n.includes('kitchen') || n.includes('dapur')) return <UtensilsCrossed size={20} color={C.amber} />;
  if (n.includes('tv') || n.includes('television')) return <Tv size={20} color={C.amber} />;
  if (n.includes('gym') || n.includes('fitness')) return <Dumbbell size={20} color={C.amber} />;
  if (n.includes('coffee') || n.includes('kopi')) return <Coffee size={20} color={C.amber} />;
  if (n.includes('game') || n.includes('gaming')) return <Gamepad2 size={20} color={C.amber} />;
  if (n.includes('fridge') || n.includes('kulkas') || n.includes('refrigerator')) return <Refrigerator size={20} color={C.amber} />;
  if (n.includes('security') || n.includes('keamanan')) return <Lock size={20} color={C.amber} />;
  if (n.includes('parking') || n.includes('parkir')) return <Car size={20} color={C.amber} />;
  if (n.includes('garden') || n.includes('taman')) return <Flower2 size={20} color={C.amber} />;
  if (n.includes('laundry') || n.includes('cuci') || n.includes('washing')) return <Shirt size={20} color={C.amber} />;
  if (n.includes('spa') || n.includes('massage')) return <Sparkles size={20} color={C.amber} />;
  return <Sparkles size={20} color={C.amber} />;
};

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const VillaDetailPage = ({
  villa: propVilla,
  onClose,
  formatCurrency: propFormatCurrency,
  isModal = false,
}) => {
  const { id }  = useParams();
  const navigate = useNavigate();

  const [villa, setVilla]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [activeImg, setActiveImg]       = useState(0);
  const [lightbox, setLightbox]         = useState(false);
  const [mousePos, setMousePos]         = useState({ x: 0, y: 0 });

  const formatCurrencyFunc = propFormatCurrency || formatCurrency;

  useEffect(() => {
    const onMouse = e => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => window.removeEventListener('mousemove', onMouse);
  }, []);

  useEffect(() => {
    if (propVilla && isModal) { setVilla(propVilla); setLoading(false); return; }
    if (id && id !== 'undefined' && !propVilla) { fetchVillaDetail(); return; }
    setLoading(false);
    if (!isModal) { toast.error('Invalid Villa ID'); navigate('/villas'); }
  }, [id, propVilla, isModal]);

  const fetchVillaDetail = async () => {
    if (!id || id === 'undefined') { setLoading(false); return; }
    try {
      setLoading(true);
      const res = await getPropertyById(id);
      if (res?.data) setVilla(res.data);
      else { toast.error('Villa not found'); if (!isModal) navigate('/villas'); }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load villa details');
      if (!isModal) navigate('/villas');
    } finally { setLoading(false); }
  };

  const handleWhatsApp = () => {
    const phone = '6281529635781';
    const checkIn  = new Date(Date.now() + 7  * 86400000);
    const checkOut = new Date(Date.now() + 14 * 86400000);
    const fmt = d => d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
    const msg = `Hello! I'm interested in the villa "${villa.title}"

Villa Details:
Location: ${villa.location?.name || villa.location_name || 'Bali'}
Price: ${formatCurrencyFunc(villa.daily_price_idr || villa.sale_price_idr, 'IDR')}${villa.daily_price_idr ? ' / night' : ''}
${villa.bedrooms} bedrooms | ${villa.bathrooms} bathrooms${villa.max_guests ? `\nMax ${villa.max_guests} guests` : ''}

Stay Plan:
- Check-in: ${fmt(checkIn)}
- Check-out: ${fmt(checkOut)}

Is this villa available for these dates?
Please provide availability information and best offers.

Thank you!`;
    const url = new URL(`https://wa.me/${phone}`);
    url.searchParams.set('text', msg);
    window.open(url.toString(), '_blank');
  };

  const handleClose    = () => { if (onClose) onClose(); else if (!isModal) navigate('/villas'); };
  const handleBackdrop = e  => { if (e.target === e.currentTarget) handleClose(); };
  const getImgUrl      = p  => {
    if (!p) return '/placeholder-villa.jpg';
    if (p.startsWith('http')) return p;
    return p.startsWith('/') ? p : `/${p}`;
  };

  const prev = () => setActiveImg(i => (i === 0 ? villa.images.length - 1 : i - 1));
  const next = () => setActiveImg(i => (i === villa.images.length - 1 ? 0 : i + 1));

  /* ── Loading ── */
  if (loading) {
    const spinner = (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 80 }}>
        <div style={{ width: 52, height: 52, border: `4px solid ${C.amberLight}`, borderTopColor: C.amber, borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
      </div>
    );
    if (isModal) return (
      <div onClick={handleBackdrop} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
        <div style={{ backgroundColor: C.white, borderRadius: 20, padding: 40 }}>{spinner}</div>
      </div>
    );
    return <MainLayout><div style={{ minHeight: '100vh' }}>{spinner}</div></MainLayout>;
  }

  /* ── Not found ── */
  if (!villa) {
    const notFound = (
      <div style={{ textAlign: 'center', padding: 60, fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🏚️</div>
        <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: C.gray900, marginBottom: 12 }}>Villa Not Found</h2>
        <BtnPrimary onClick={handleClose} style={{ padding: '12px 26px' }}>
          <ArrowLeft size={16} /> {isModal ? 'Close' : 'Back to Villas'}
        </BtnPrimary>
      </div>
    );
    if (isModal) return (
      <div onClick={handleBackdrop} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
        <div style={{ backgroundColor: C.white, borderRadius: 20 }}>{notFound}</div>
      </div>
    );
    return <MainLayout><div className="container" style={{ padding: '40px 24px' }}>{notFound}</div></MainLayout>;
  }

  const images = villa.images?.length > 0 ? villa.images : [{ image_path: villa.primary_image }];
  const currentImgUrl = getImgUrl(images[activeImg]?.image_path || villa.primary_image);

  /* ══ VILLA CONTENT ══ */
  const VillaContent = () => (
    <div style={{ backgroundColor: C.stone50, fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── Image Gallery ── */}
      <div style={{ position: 'relative', backgroundColor: C.gray900 }}>

        {/* Main image */}
        <div style={{ position: 'relative', height: 'clamp(280px, 55vh, 650px)', overflow: 'hidden' }}>
          <img src={currentImgUrl} alt={villa.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s ease' }}
            onError={e => { e.target.src = '/placeholder-villa.jpg'; }}
          />
          {/* Gradient */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.35), transparent 60%)' }} />

          {/* Top bar */}
          <div style={{ position: 'absolute', top: 16, left: 16, right: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {/* Featured badge */}
            {villa.featured && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 16px', backgroundColor: C.amber, color: C.white, borderRadius: 50, fontSize: 13, fontWeight: 700, boxShadow: '0 4px 14px rgba(217,119,6,0.4)' }}>
                <Star size={13} fill={C.white} /> Featured Villa
              </span>
            )}
            <div style={{ display: 'flex', gap: 10, marginLeft: 'auto' }}>
              {/* Zoom/lightbox */}
              <IconBtn onClick={() => setLightbox(true)}>
                <ZoomIn size={18} />
              </IconBtn>
              {/* Close (modal) */}
              {isModal && (
                <IconBtn onClick={handleClose}>
                  <X size={18} />
                </IconBtn>
              )}
            </div>
          </div>

          {/* Back button (standalone page) */}
          {!isModal && (
            <button onClick={() => navigate('/villas')}
              style={{ position: 'absolute', top: 16, left: 16, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 18px', backgroundColor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', borderRadius: 50, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: C.gray900, fontFamily: 'inherit', transition: 'background 0.2s', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = C.white}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.9)'}
            >
              <ArrowLeft size={15} /> Back to Villas
            </button>
          )}

          {/* Nav arrows */}
          {images.length > 1 && (
            <>
              <div style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}>
                <IconBtn onClick={prev}><ChevronLeft size={20} /></IconBtn>
              </div>
              <div style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)' }}>
                <IconBtn onClick={next}><ChevronRight size={20} /></IconBtn>
              </div>
            </>
          )}

          {/* Image counter */}
          {images.length > 1 && (
            <div style={{ position: 'absolute', bottom: 16, right: 16, padding: '6px 14px', backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', borderRadius: 50, color: C.white, fontSize: 13, fontWeight: 600 }}>
              {activeImg + 1} / {images.length}
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div style={{ backgroundColor: C.white, padding: '16px 24px', borderBottom: `1px solid ${C.gray100}` }}>
            <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
              {images.map((img, idx) => (
                <button key={img.id || idx} onClick={() => setActiveImg(idx)} style={{
                  flexShrink: 0, width: 90, height: 72, borderRadius: 10, overflow: 'hidden',
                  border: `2.5px solid ${activeImg === idx ? C.amber : C.gray200}`,
                  boxShadow: activeImg === idx ? `0 0 0 3px rgba(217,119,6,0.2)` : 'none',
                  cursor: 'pointer', padding: 0, background: 'none',
                  transition: 'all 0.25s ease',
                  transform: activeImg === idx ? 'scale(1.05)' : 'scale(1)',
                }}>
                  <img src={getImgUrl(img.image_path)} alt={`Thumb ${idx + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.src = '/placeholder-villa.jpg'; }} />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Body ── */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '40px 24px', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 32, alignItems: 'start' }}>

        {/* ── LEFT COLUMN ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

          {/* Header card */}
          <Anim dir="up" delay={0}>
            <div style={{ backgroundColor: C.white, borderRadius: 20, padding: '32px 36px', boxShadow: '0 6px 28px rgba(0,0,0,0.07)', border: `1px solid ${C.gray100}` }}>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 700, color: C.gray900, lineHeight: 1.1, marginBottom: 12 }}>
                {villa.title}
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: C.gray500, marginBottom: 24, fontSize: 16 }}>
                <MapPin size={17} color={C.amber} />
                <span style={{ fontWeight: 500 }}>{villa.location?.name || villa.location_name || 'Bali, Indonesia'}</span>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12 }}>
                <StatCard emoji="🛏️" value={villa.bedrooms}          label="Bedrooms"  />
                <StatCard emoji="🛁" value={villa.bathrooms}         label="Bathrooms" />
                {villa.max_guests      && <StatCard emoji="👥" value={villa.max_guests}      label="Guests"    />}
                {villa.property_size_sqm && <StatCard emoji="📐" value={`${villa.property_size_sqm}m²`} label="Area" />}
              </div>
            </div>
          </Anim>

          {/* Description */}
          <Anim dir="up" delay={80}>
            <div style={{ backgroundColor: C.white, borderRadius: 20, padding: '32px 36px', boxShadow: '0 6px 28px rgba(0,0,0,0.07)', border: `1px solid ${C.gray100}` }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(22px,3vw,32px)', fontWeight: 700, color: C.gray900, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>📖</span> About This Villa
              </h2>
              <div style={{ color: C.gray600, lineHeight: 1.85, fontSize: 16 }}>
                {villa.description
                  ? <div dangerouslySetInnerHTML={{ __html: villa.description }} />
                  : <p>Luxury villa with complete facilities for your perfect vacation in Bali.</p>
                }
              </div>
            </div>
          </Anim>

          {/* Facilities */}
          {villa.facilities?.length > 0 && (
            <Anim dir="up" delay={160}>
              <div style={{ backgroundColor: C.white, borderRadius: 20, padding: '32px 36px', boxShadow: '0 6px 28px rgba(0,0,0,0.07)', border: `1px solid ${C.gray100}` }}>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(22px,3vw,32px)', fontWeight: 700, color: C.gray900, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>✨</span> Amenities & Facilities
                </h2>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                  {villa.facilities.map((f, idx) => (
                    <FacilityItem key={f.id || idx} name={f.name} />
                  ))}
                </div>
              </div>
            </Anim>
          )}

          {/* Location info */}
          {(villa.location?.name || villa.location_name) && (
            <Anim dir="up" delay={240}>
              <div style={{ backgroundColor: C.white, borderRadius: 20, padding: '32px 36px', boxShadow: '0 6px 28px rgba(0,0,0,0.07)', border: `1px solid ${C.gray100}` }}>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(22px,3vw,32px)', fontWeight: 700, color: C.gray900, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span>📍</span> Location
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', backgroundColor: C.amberLight, borderRadius: 12, border: `1px solid #fde68a` }}>
                  <MapPin size={20} color={C.amber} />
                  <span style={{ fontWeight: 600, color: C.gray900, fontSize: 16 }}>{villa.location?.name || villa.location_name}, Bali, Indonesia</span>
                </div>
              </div>
            </Anim>
          )}
        </div>

        {/* ── RIGHT COLUMN (sticky booking card) ── */}
        <div style={{ position: 'sticky', top: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Price card */}
          <Anim dir="right" delay={100}>
            <div style={{ backgroundColor: C.white, borderRadius: 20, padding: '28px', boxShadow: '0 12px 40px rgba(0,0,0,0.12)', border: `2px solid #fde68a` }}>

              {/* Price */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 40, fontWeight: 700, color: C.amber, lineHeight: 1, marginBottom: 4 }}>
                  {formatCurrencyFunc(villa.daily_price_idr || villa.sale_price_idr, 'IDR')}
                </div>
                {(villa.daily_price_usd || villa.sale_price_usd) && (
                  <div style={{ color: C.gray500, fontSize: 15, marginBottom: 4 }}>
                    ≈ {formatCurrencyFunc(villa.daily_price_usd || villa.sale_price_usd, 'USD')}
                  </div>
                )}
                {villa.daily_price_idr && (
                  <div style={{ fontSize: 12, color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.1em' }}>per night</div>
                )}
              </div>

              {/* Rating */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', backgroundColor: C.amberLight, borderRadius: 12, marginBottom: 20 }}>
                <Star size={16} fill="#fbbf24" color="#fbbf24" />
                <span style={{ fontWeight: 700, color: C.gray900 }}>5.0</span>
                <span style={{ color: C.gray500, fontSize: 13 }}>Excellent Reviews</span>
              </div>

              {/* Divider */}
              <div style={{ height: 1, backgroundColor: C.gray100, marginBottom: 20 }} />

              {/* WhatsApp CTA */}
              <BtnWhatsApp onClick={handleWhatsApp} style={{ marginBottom: 14 }}>
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                Book via WhatsApp
              </BtnWhatsApp>

              {/* Quick info */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { icon: <Clock size={14} color={C.amber} />, text: 'Quick response within 5 minutes' },
                  { icon: <span style={{ fontSize: 14 }}>💬</span>, text: 'Free consultation available' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.gray500, fontSize: 13 }}>
                    {item.icon} {item.text}
                  </div>
                ))}
              </div>
            </div>
          </Anim>

          {/* Trust badges */}
          <Anim dir="right" delay={200}>
            <div style={{ background: `linear-gradient(135deg, ${C.amberLight}, #fff7ed)`, borderRadius: 20, padding: '24px', border: `1px solid #fde68a` }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: C.gray900, textAlign: 'center', marginBottom: 18 }}>Why Choose Us</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { icon: <CheckCircle2 size={22} color={C.amber} />, title: 'Verified Property', desc: '100% authentic photos & info' },
                  { icon: <Shield size={22} color={C.amber} />,       title: 'Secure Booking',   desc: 'Protected payment process' },
                  { icon: <Star size={22} color={C.amber} fill={C.amber} />, title: 'Premium Service', desc: '24/7 customer support' },
                ].map((item, i) => (
                  <TrustItem key={i} icon={item.icon} title={item.title} desc={item.desc} />
                ))}
              </div>
            </div>
          </Anim>

          {/* Pricing summary */}
          {(villa.monthly_price_idr || villa.yearly_price_idr) && (
            <Anim dir="right" delay={300}>
              <div style={{ backgroundColor: C.white, borderRadius: 20, padding: '24px', boxShadow: '0 6px 24px rgba(0,0,0,0.07)', border: `1px solid ${C.gray100}` }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: C.gray900, marginBottom: 14 }}>Pricing Options</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {villa.daily_price_idr && <PriceRow label="Per Night" value={formatCurrencyFunc(villa.daily_price_idr, 'IDR')} highlight />}
                  {villa.monthly_price_idr && <PriceRow label="Per Month" value={formatCurrencyFunc(villa.monthly_price_idr, 'IDR')} />}
                  {villa.yearly_price_idr  && <PriceRow label="Per Year"  value={formatCurrencyFunc(villa.yearly_price_idr, 'IDR')} />}
                </div>
              </div>
            </Anim>
          )}
        </div>
      </div>
    </div>
  );

  /* ── Lightbox ── */
  if (lightbox) return (
    <>
      <div onClick={() => setLightbox(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconBtn onClick={() => setLightbox(false)} style={{ position: 'absolute', top: 20, right: 20, width: 48, height: 48 }}>
          <X size={22} />
        </IconBtn>
        {images.length > 1 && (
          <IconBtn onClick={e => { e.stopPropagation(); prev(); }} style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', width: 52, height: 52 }}>
            <ChevronLeft size={24} />
          </IconBtn>
        )}
        <img src={currentImgUrl} alt={villa.title} onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 24px 80px rgba(0,0,0,0.6)' }} />
        {images.length > 1 && (
          <IconBtn onClick={e => { e.stopPropagation(); next(); }} style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', width: 52, height: 52 }}>
            <ChevronRight size={24} />
          </IconBtn>
        )}
        <div style={{ position: 'absolute', bottom: 24, color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>{activeImg + 1} / {images.length}</div>
      </div>
      {/* Still show content behind */}
      {isModal
        ? <div onClick={handleBackdrop} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}><div style={{ backgroundColor: C.white, borderRadius: 20, maxWidth: 1280, width: '100%', maxHeight: '95vh', overflowY: 'auto' }}><VillaContent /></div></div>
        : <MainLayout><VillaContent /></MainLayout>
      }
    </>
  );

  /* ── Modal mode ── */
  if (isModal) return (
    <div onClick={handleBackdrop} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
      <div style={{ backgroundColor: C.white, borderRadius: 24, maxWidth: 1280, width: '100%', maxHeight: '95vh', overflowY: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.35)' }}>
        <VillaContent />
      </div>
    </div>
  );

  /* ── Standalone page ── */
  return (
    <MainLayout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
      <VillaContent />
    </MainLayout>
  );
};

/* ── Sub-components ── */
const FacilityItem = ({ name }) => {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 16px',
        background: hov ? 'linear-gradient(135deg, #fffbeb, #fff7ed)' : `linear-gradient(135deg, ${C.amberLight}, #fff7ed)`,
        borderRadius: 12, border: `1px solid ${hov ? C.amber : '#fde68a'}`,
        transition: 'all 0.25s ease',
        transform: hov ? 'translateX(4px)' : 'none',
        boxShadow: hov ? '0 4px 14px rgba(217,119,6,0.15)' : 'none',
        cursor: 'default',
      }}>
      <div style={{ flexShrink: 0 }}>{getFacilityIcon(name)}</div>
      <span style={{ color: C.gray800 || C.gray700, fontWeight: 500, fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>{name}</span>
    </div>
  );
};

const TrustItem = ({ icon, title, desc }) => {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '10px 12px', borderRadius: 12,
        backgroundColor: hov ? 'rgba(255,255,255,0.7)' : 'transparent',
        transition: 'background 0.2s ease', cursor: 'default',
      }}>
      <div style={{ flexShrink: 0, marginTop: 1 }}>{icon}</div>
      <div>
        <div style={{ fontWeight: 600, color: C.gray900, fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>{title}</div>
        <div style={{ fontSize: 12, color: C.gray500, fontFamily: "'DM Sans', sans-serif" }}>{desc}</div>
      </div>
    </div>
  );
};

const PriceRow = ({ label, value, highlight = false }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, backgroundColor: highlight ? C.amberLight : C.gray50, border: `1px solid ${highlight ? '#fde68a' : C.gray100}` }}>
    <span style={{ fontSize: 13, color: C.gray600, fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
    <span style={{ fontWeight: 700, color: highlight ? C.amber : C.gray900, fontSize: 14, fontFamily: "'DM Sans', sans-serif" }}>{value}</span>
  </div>
);

export default VillaDetailPage;